'use client'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { CheckCircle2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import UpgradeButton from './UpgradeButton'
import ManageBillingButton from './ManageBillingButton'
import {
  PLANS,
  planFeatures,
  PRO_TERMS,
  formatUsd,
  BILLING_INTERVALS,
  type PlanKey,
  type BillingInterval,
} from '@/lib/billing/plan-config'

const TERM_LABEL: Record<BillingInterval, string> = {
  monthly: 'Monthly',
  sixmo: '6 months',
  annual: 'Annual',
}
import { ReferralBanner } from '@/components/referral/ReferralBanner'
import { COMPETITOR_PRICING, competitorCheckedOnLabel } from '@/lib/marketing/competitor-pricing'
import { getStoredRefCode } from '@/lib/referral/ref-code'

export type PlanMeta = {
  planKey: PlanKey
  isCurrent: boolean
  isHighlighted: boolean
  canUpgrade: boolean
  canDowngrade: boolean
  showTrial: boolean
  /** Quiet trial-status note for the user's CURRENT plan card while trialing —
   *  e.g. "Free trial ends June 14, 2026 — billing starts then on your saved
   *  payment method." Null when not trialing / not the current plan. */
  trialNote?: string | null
  /**
   * True when this tier is "current" ONLY because of a complimentary grant.
   *
   * getEffectivePlan returns the comped tier, so a comped user's Pro card took
   * the isCurrent branch — which shows the words "Current plan" and, since a
   * comp has no Stripe customer, no button beside them. The result was a page
   * that told someone they already had Pro and offered them no way to buy it,
   * which is precisely the wrong answer on the day their grant runs out.
   */
  isComp?: boolean
}

interface Props {
  plans: PlanMeta[]
  isLoggedIn: boolean
  /** Same-origin path to land on after checkout (already sanitized by the
   *  pricing page) — threads through to the checkout call. */
  returnTo?: string | null
  /** Whether the user has a Stripe customer (a real subscription) behind their
   *  current tier. A comp-access user is on a paid tier but has NO Stripe
   *  customer, so the "Manage billing" button (Stripe portal) would 400 for
   *  them — gate it on this. */
  hasStripeBilling?: boolean
  /** The LAST DAY of the complimentary grant ("Oct 14, 2026"), already
   *  formatted by the server with compLastDayLabel — worded "through". Not the
   *  stored end: that is the exclusive instant Pro stops, and formatting it here
   *  would name the wrong day (and in the server's or browser's zone). */
  compLastDayLabel?: string | null
  /** Billing terms whose Stripe price is configured (server-resolved). Only
   *  these are selectable, so the displayed price always equals the charged one.
   *  Ordered low→high commitment; defaults to monthly-only. */
  availableTerms?: BillingInterval[]
  /** A Benchmark is open to students right now (benchmark-availability.ts,
   *  server-resolved). Off = the cards don't promise one. */
  benchmarkAvailable?: boolean
}

export default function PricingGrid({
  plans,
  isLoggedIn,
  returnTo,
  hasStripeBilling = false,
  compLastDayLabel = null,
  availableTerms = ['monthly'],
  benchmarkAvailable = false,
}: Props) {
  const [referralCode, setReferralCode] = useState('')
  const [showCodeInput, setShowCodeInput] = useState(false)
  // Packaging v2: billing-term toggle. Default to the most-committed CONFIGURED
  // term (annual when live) so we headline the best value — never a term whose
  // Stripe price isn't set (which would silently bill monthly).
  const [term, setTerm] = useState<BillingInterval>(
    availableTerms[availableTerms.length - 1] ?? 'monthly',
  )
  const proTerm = PRO_TERMS[term]

  // (c) Pre-populate from sessionStorage or cookie if a ?ref= code was
  // stashed earlier (e.g. via the invite email link → /signup?ref=CODE).
  useEffect(() => {
    if (!isLoggedIn) return
    const stored = getStoredRefCode()
    if (stored) {
      setReferralCode(stored)
      setShowCodeInput(true)
    }
  }, [isLoggedIn])

  const hasUpgradeable = plans.some((p) => p.canUpgrade)

  return (
    <div>
      {/* Show referral banner when a code is present — either pre-filled
          from the invite link (REF-8 passthrough) or typed manually. */}
      {referralCode && <ReferralBanner code={referralCode} />}

      {isLoggedIn && hasUpgradeable && (
        <div className="mb-4">
          <button
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => setShowCodeInput((v) => !v)}
          >
            Have a referral code?
          </button>
          {showCodeInput && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.trim())}
                placeholder="Enter code"
                maxLength={32}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </div>
      )}

      {/* Billing-term toggle — only configured terms are selectable, so the
          shown price always matches what Stripe charges. Hidden when there's no
          real choice (only monthly live). */}
      {availableTerms.length > 1 && (
        <div className="mb-6 flex flex-col items-center gap-2">
          <div
            className="inline-flex rounded-full border border-border bg-card p-1 text-sm"
            role="tablist"
            aria-label="Billing term"
          >
            {BILLING_INTERVALS.filter((iv) => availableTerms.includes(iv)).map((iv) => (
              <button
                key={iv}
                role="tab"
                aria-selected={term === iv}
                onClick={() => setTerm(iv)}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  term === iv
                    ? 'bg-brand text-on-brand'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {TERM_LABEL[iv]}
              </button>
            ))}
          </div>
          {/* Always render the badge line — an invisible placeholder for terms
              with no badge (e.g. Monthly) — so switching terms keeps the toggle
              the same height and doesn't shift the cards below (#pricing-jump). */}
          <span
            data-testid="term-badge"
            className={`min-h-[1rem] text-xs font-semibold text-emerald-600 dark:text-emerald-400 ${
              proTerm.badge ? '' : 'invisible'
            }`}
          >
            {proTerm.badge || ' '}
          </span>
        </div>
      )}

      {/* Two tiers (Free + Pro) — a centered, max-width pair. (Was lg:grid-cols-3
          for the retired 3-tier layout, which left the two cards hugging the left
          third of a max-w-7xl page and looking off-centre.) */}
      <div className="mx-auto grid max-w-3xl gap-5 sm:grid-cols-2">
        {plans.map(
          ({
            planKey,
            isCurrent,
            isComp,
            isHighlighted,
            canUpgrade,
            canDowngrade,
            showTrial,
            trialNote,
          }) => {
            const plan = PLANS[planKey]
            return (
              <Card
                key={planKey}
                className={`relative flex flex-col p-6 ${
                  isCurrent ? 'ring-2 ring-primary' : ''
                } ${isHighlighted ? 'border-2 border-brand dark:border-brand' : ''}`}
              >
                <div className="mb-1 text-sm font-medium text-muted-foreground">{plan.name}</div>
                {planKey === 'FREE' ? (
                  <div className="mb-3 text-3xl font-bold">$0</div>
                ) : (
                  <div className="mb-3">
                    <div data-testid="term-per-month" className="text-3xl font-bold">
                      {formatUsd(proTerm.perMonth)}
                      <span className="text-base font-normal text-muted-foreground"> / month</span>
                    </div>
                    {/* The exact charge and that it recurs — the headline is a
                        per-month equivalent, so the buyer needs both to know
                        what they will actually be billed, and when. */}
                    <div data-testid="term-charge" className="text-xs text-muted-foreground">
                      {proTerm.totalLabel} · {proTerm.renewalLabel} until you cancel
                    </div>
                  </div>
                )}
                <p className="mb-4 text-sm text-muted-foreground">{plan.tagline}</p>

                <ul className="mb-6 flex-1 space-y-2 text-sm">
                  {planFeatures(planKey, { benchmarkAvailable }).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                      <span className="text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  {!isLoggedIn ? (
                    planKey === 'FREE' ? (
                      <Link
                        href="/signin"
                        className="inline-flex h-9 w-full items-center justify-center rounded-md border border-border px-4 text-sm text-foreground hover:bg-muted"
                      >
                        Sign up free
                      </Link>
                    ) : (
                      <Link
                        href="/signin"
                        className="inline-flex h-9 w-full items-center justify-center rounded-md bg-brand px-4 text-sm font-medium text-on-brand hover:bg-brand-hover"
                      >
                        Get started
                      </Link>
                    )
                  ) : isCurrent && isComp && planKey !== 'FREE' ? (
                    /* Complimentary, not bought. They DO have the tier today,
                       so this is not an upgrade — but it is the only place they
                       can arrange to keep it, and the old branch gave them a
                       label and nothing to press. */
                    <>
                      <UpgradeButton
                        plan={planKey}
                        planName={plan.name}
                        trial={false}
                        interval={term}
                        referralCode={referralCode || undefined}
                        returnTo={returnTo}
                      />
                      <p className="mt-2 text-xs text-muted-foreground">
                        {compLastDayLabel
                          ? `You're on complimentary ${plan.name} through ${compLastDayLabel}. Subscribe whenever you like — billing starts today and you keep everything you have now.`
                          : `You're on complimentary ${plan.name}. Subscribe whenever you like — billing starts today and you keep everything you have now.`}
                      </p>
                    </>
                  ) : isCurrent ? (
                    <div>
                      <div className="flex items-center gap-2">
                        {planKey !== 'FREE' && hasStripeBilling && <ManageBillingButton />}
                        <span className="text-xs text-muted-foreground">Current plan</span>
                      </div>
                      {trialNote && (
                        <p className="mt-2 text-xs text-muted-foreground">{trialNote}</p>
                      )}
                    </div>
                  ) : canUpgrade ? (
                    <>
                      <UpgradeButton
                        plan={planKey}
                        planName={plan.name}
                        trial={showTrial}
                        interval={term}
                        referralCode={referralCode || undefined}
                        returnTo={returnTo}
                      />
                      {/* Packaging v2: the 7-day trial is retired (showTrial is
                          always false); the value is the free tier + cancel-anytime. */}
                      {planKey === 'PRO' ? (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Cancel anytime to stop the next renewal — you keep access through the end
                          of the period you paid for. Unused time isn&apos;t refunded.
                        </p>
                      ) : null}
                    </>
                  ) : canDowngrade && hasStripeBilling ? (
                    <ManageBillingButton />
                  ) : null}
                  {/* Clickwrap notice at the payment door — /pricing has no
                      global footer, so these are the ONLY Terms/Privacy links a
                      buyer sees before paying (go-live audit). Rendered for
                      EVERY visitor state (incl. anonymous), both so the notice
                      is genuinely pre-decision and so the promote-gate e2e can
                      assert it without auth. */}
                  {planKey === 'PRO' && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      By subscribing you agree to our{' '}
                      <Link href="/terms" className="underline hover:text-foreground">
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link href="/privacy" className="underline hover:text-foreground">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  )}
                </div>
              </Card>
            )
          },
        )}
      </div>

      {/* Value anchor: competitors' prices exactly as their pages state them,
          with the billing basis and the day we checked (competitor-pricing.ts).
          No strike-throughs: facts, not a sneer. */}
      <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="pricing-compare">
        For comparison, as listed on {competitorCheckedOnLabel()}:{' '}
        {COMPETITOR_PRICING.filter((c) => c.plans.length > 0).map((c, i) => (
          <span key={c.key}>
            {i > 0 ? ' · ' : ''}
            {c.name}{' '}
            {c.verified
              ? c.plans.map((p) => `${p.name} ${p.upfront} ${p.billing}`).join(', ')
              : 'not verified'}
          </span>
        ))}
        .{' '}
        <Link href="/#compare" className="underline hover:text-foreground">
          Full comparison
        </Link>
        . No hidden fees, cancel anytime.
      </p>
    </div>
  )
}
